import {EvaluatedParameters, Evaluator} from "@cm/graph-v2-final"
import {ImageOpContextHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-hal"
import {AnyImageOpHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal"
import {ImageOpDefinition, ImageOpInputsOrNodes} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {
    ImageOpOutputsOfType,
    ImageOpParametersOfType,
    SpecializingImageOpNode,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/specializing-image-op-node"
import {ImageOpContextImgProc} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-imgproc"
import {REF_COUNT_DEBUG_ID_INITIAL} from "@cm/utils"

export abstract class ComplexSpecializingImageOpNode<
    Definition extends ImageOpDefinition,
    ImageOpHalClass extends AnyImageOpHal,
> extends SpecializingImageOpNode<Definition> {
    protected abstract createImageOpHal(
        evaluator: Evaluator<ImageOpContextHal>,
        evaluatedParameters: EvaluatedParameters<ImageOpParametersOfType<Definition, "hal">>,
    ): Promise<ImageOpHalClass>
    protected abstract updateImageOpHal(
        evaluator: Evaluator<ImageOpContextHal>,
        imageOpHal: ImageOpHalClass,
        evaluatedParameters: EvaluatedParameters<ImageOpParametersOfType<Definition, "hal">>,
    ): Promise<void>
    protected abstract getHalResult(
        evaluator: Evaluator<ImageOpContextHal>,
        imageOpHal: ImageOpHalClass,
        evaluatedParameters: EvaluatedParameters<ImageOpParametersOfType<Definition, "hal">>,
    ): ImageOpOutputsOfType<Definition, "hal">

    constructor(parameters: ImageOpInputsOrNodes<Definition>) {
        super(parameters)
    }

    protected override dispose() {
        this.releaseHalOutput()
        super.dispose()
    }

    protected releaseHalOutput() {
        this.halOutput?.release(REF_COUNT_DEBUG_ID_INITIAL)
        this.halOutput = null // no need to call release as we don't explicitly keep a reference
    }

    protected async toHal(
        evaluator: Evaluator<ImageOpContextHal>,
        parameters: ImageOpParametersOfType<Definition, "hal">,
    ): Promise<ImageOpOutputsOfType<Definition, "hal">> {
        const evaluatedParameters = await evaluator.getAll(parameters)
        if (!this.halOutput) {
            this.halOutput = await this.createImageOpHal(evaluator, evaluatedParameters)
            this.halOutput.debugId = this.debugId
        } else {
            await this.updateImageOpHal(evaluator, this.halOutput, evaluatedParameters)
        }
        return this.getHalResult(evaluator, this.halOutput, evaluatedParameters)
    }

    protected toImgProc(
        _evaluator: Evaluator<ImageOpContextImgProc>,
        _parameters: ImageOpParametersOfType<Definition, "imgproc">,
    ): Promise<ImageOpOutputsOfType<Definition, "imgproc">> {
        throw new Error(`Method toImgProc not implemented for ${this.nodeClass}`)
    }

    private halOutput: ImageOpHalClass | null = null
}
